import deepEqual from 'fast-deep-equal';
import { Log, LogCategory } from '@augloop/logging';
import { DataListener } from './data-listener';
/**
 * Factory for settings and manager of the current composite configuration
 */
export class Setting extends DataListener {
    constructor(name) {
        super();
        this.name = name;
    }
    /**
     * Retrives or creates a setting with the given name
     * @param name name of the setting
     */
    static getInstance(name) {
        let setting = this.allSettings.get(name);
        if (!setting) {
            setting = new Setting(name);
            const result = Setting.tryGetConfigProperty(Setting.currentConfig, name);
            if (result.success) {
                setting.updateValue(result.value);
            }
            this.allSettings.set(name, setting);
        }
        return setting;
    }
    /**
     * Retrives or creates a setting with the given pattern
     * @param pattern pattern for the setting names included in the setting
     */
    static getPatternInstance(pattern) {
        let settingRegexPair = this.allPatternSettings.get(pattern);
        if (!settingRegexPair) {
            // eslint-disable-next-line security/detect-non-literal-regexp
            settingRegexPair = { regex: new RegExp(pattern), setting: new Setting(pattern) };
            const value = Setting.tryGetConfigPropertyByPattern(Setting.currentConfig, settingRegexPair.regex);
            settingRegexPair.setting.updateValue(value);
            this.allPatternSettings.set(pattern, settingRegexPair);
        }
        return settingRegexPair.setting;
    }
    /**
     * Applies a new composite configuration to the current set of settings
     * @param config new config to be applied
     */
    static setNewConfig(config) {
        Setting.currentConfig = config;
        Log.info(0x2224d04e /* tag_8jnbo */, LogCategory.CoreDefault, `New config: ${JSON.stringify(config)}`);
        Setting.allSettings.forEach((setting, name) => {
            const value = Setting.tryGetConfigProperty(Setting.currentConfig, name).value;
            if (setting.updateValue(value)) {
                Log.info(0x2224d04f /* tag_8jnbp */, LogCategory.CoreDefault, `Setting new value for ${name}: ${value instanceof Object ? JSON.stringify(value) : value}`);
                setting.notifyListeners(value);
            }
        });
        Setting.allPatternSettings.forEach((settingRegexPair, pattern) => {
            const value = Setting.tryGetConfigPropertyByPattern(config, settingRegexPair.regex);
            if (settingRegexPair.setting.updateValue(value)) {
                Log.info(0x1e4e1186 /* tag_4t7gg */, LogCategory.CoreDefault, `Setting new value for pattern ${pattern}: ${JSON.stringify(value)}`);
                settingRegexPair.setting.notifyListeners(value);
            }
        });
    }
    static tryGetConfigProperty(config, name) {
        if (config && config.hasOwnProperty(name)) {
            return { success: true, value: Setting.selectApplicableValue(config[name]) };
        }
        return { success: false, value: undefined };
    }
    static tryGetConfigPropertyByPattern(config, pattern) {
        const result = [];
        if (config) {
            const settingNames = Object.getOwnPropertyNames(config).filter(settingName => pattern.test(settingName));
            for (const settingName of settingNames) {
                result.push({
                    name: settingName,
                    value: Setting.selectApplicableValue(config[settingName])
                });
            }
        }
        return result;
    }
    /**
     * Sets up a predicate to filter settings from the composite configuration
     * @param filter filtering predicate
     */
    static setGlobalFilter(filter) {
        Setting.globalFilter = filter;
    }
    /**
     * Clears all settings
     */
    static clear() {
        Setting.currentConfig = undefined;
        Setting.allSettings.clear();
        Setting.allPatternSettings.clear();
    }
    static selectApplicableValue(values) {
        let value;
        if (values) {
            const filteredResult = values.find((val) => {
                return !Setting.globalFilter || Setting.globalFilter(val);
            });
            if (filteredResult) {
                value = filteredResult.value;
            }
        }
        return value;
    }
    /**
     * Returns setting name
     */
    getName() {
        return this.name;
    }
    /**
     * Returns setting value
     */
    getValue() {
        var _a, _b;
        if (typeof globalThis.process !== 'undefined' && ((_b = (_a = globalThis.process) === null || _a === void 0 ? void 0 : _a.env) === null || _b === void 0 ? void 0 : _b.VALIDATE_CORRECT_USAGE_OF_GET_VALUE) === 'true') {
            throw new Error('getValue() has been invoked incorrectly for ModuleSettings. Make sure getValue() is called dynamically where the value of a setting is needed');
        }
        return this.value;
    }
    updateValue(value) {
        if (!deepEqual(this.value, value)) {
            Log.info(0x2224d050 /* tag_8jnbq */, LogCategory.CoreDefault, `Received new property value for ${this.getName()}:`, value);
            this.value = value;
            return true;
        }
        return false;
    }
}
Setting.allSettings = new Map();
Setting.allPatternSettings = new Map();
/**
 * A type-safe instance of a given setting
 * with an optional default value
 */
export class SettingInstance extends DataListener {
    constructor(name, defaultValue) {
        super();
        this.listenerId = NaN;
        this.defaultValue = defaultValue;
        this.setting = Setting.getInstance(name);
    }
    /**
     * Returns default value for this instance
     */
    getDefaultValue() {
        return this.defaultValue;
    }
    addListener(callback) {
        if (Number.isNaN(this.listenerId)) {
            this.listenerId = this.setting.addListener(() => {
                this.notifyListeners(this.getValue());
            });
        }
        return super.addListener(callback);
    }
    removeListener(id) {
        super.removeListener(id);
        if (this.listeners.size == 0 && !Number.isNaN(this.listenerId)) {
            this.setting.removeListener(this.listenerId);
            this.listenerId = NaN;
        }
    }
    /**
     * Returns the current setting value
     */
    getValue() {
        const value = this.setting.getValue();
        return (value === undefined) ? this.defaultValue : value;
    }
}
/**
 * A type-safe instance of an array of settings with names matching given pattern
 */
export class SettingPatternInstance extends DataListener {
    constructor(pattern) {
        super();
        this.listenerId = NaN;
        this.setting = Setting.getPatternInstance(pattern);
    }
    addListener(callback) {
        if (Number.isNaN(this.listenerId)) {
            this.listenerId = this.setting.addListener(() => {
                this.notifyListeners(this.getValue());
            });
        }
        return super.addListener(callback);
    }
    removeListener(id) {
        super.removeListener(id);
        if (this.listeners.size == 0 && !Number.isNaN(this.listenerId)) {
            this.setting.removeListener(this.listenerId);
            this.listenerId = NaN;
        }
    }
    /**
     * Returns the current setting value
     */
    getValue() {
        const value = this.setting.getValue();
        return (value === undefined) ? [] : value;
    }
}
//# sourceMappingURL=setting.js.map